import { useState, useEffect } from 'react';
import { doc, updateDoc, deleteDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Invoice } from '../models/Invoice';

export function useInvoice(invoiceId: string | null) {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!invoiceId) {
      setInvoice(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const invoiceRef = doc(db, 'invoices', invoiceId);

    const unsubscribe = onSnapshot(
      invoiceRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setInvoice({
            id: snapshot.id,
            ...data,
            date: data.date?.toDate() || new Date(),
            dueDate: data.dueDate?.toDate() || new Date(),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Invoice);
          setError(null);
        } else {
          setError(new Error('Invoice not found'));
          setInvoice(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching invoice:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [invoiceId]);

  const updateInvoice = async (updates: Partial<Invoice>) => {
    if (!invoiceId) throw new Error('Invoice ID is required');

    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.now(),
      };

      if (updates.date) {
        updateData.date = Timestamp.fromDate(updates.date);
      }
      if (updates.dueDate) {
        updateData.dueDate = Timestamp.fromDate(updates.dueDate);
      }

      await updateDoc(invoiceRef, updateData);
    } catch (err) {
      console.error('Error updating invoice:', err);
      throw err;
    }
  };

  const deleteInvoice = async () => {
    if (!invoiceId) throw new Error('Invoice ID is required');

    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);
      await deleteDoc(invoiceRef);
    } catch (err) {
      console.error('Error deleting invoice:', err);
      throw err;
    }
  };

  return {
    invoice,
    loading,
    error,
    updateInvoice,
    deleteInvoice,
  };
}
